// src/pages/DashboardPartenaires.js - Suivi des sommes collectées/retirées chez NITA/AMANA
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  danger: '#f44336',
  success: '#4caf50',
};

const GATEWAY_META = {
  nita: { label: 'NITA', color: '#F5871F', logo: '/assets/img/mynita_logo.png' },
  amana: { label: 'Amana', color: '#1E9E5A', logo: '/assets/img/amana_logo.png' },
};

const formatMontant = (n) => new Intl.NumberFormat('fr-FR').format(n || 0) + ' XOF';

const DashboardPartenaires = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showRetraitModal, setShowRetraitModal] = useState(false);
  const [retraitGateway, setRetraitGateway] = useState('nita');
  const [retraitMontant, setRetraitMontant] = useState("");
  const [retraitDate, setRetraitDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [retraitNote, setRetraitNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [showListModal, setShowListModal] = useState(false);
  const [listGateway, setListGateway] = useState(null); // null = toutes
  const [retraits, setRetraits] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("access_token")}` });

  // Le backend (retraits/stats.php, create.php, list.php) refuse déjà tout
  // non-admin — garde de renfort côté page (données financières sensibles).
  let currentProfile = 0;
  try {
    currentProfile = Number(JSON.parse(localStorage.getItem("user") || "{}")?.profile || 0);
  } catch (e) { /* ignore */ }
  const isAdmin = currentProfile === 1;

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE_URL}/retraits/stats.php`, { headers: authHeaders() });
      if (res.data?.success) {
        setStats(res.data);
      } else {
        setError(res.data?.message || "Erreur lors du chargement.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Impossible de charger les statistiques.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchStats();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStats]);

  if (!isAdmin) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '700', fontSize: '1.8rem' }}>
            Retraits partenaires
          </h1>
        </div>
        <section className="section">
          <div className="alert alert-warning">
            Accès réservé à l'administrateur.
          </div>
        </section>
      </Layout>
    );
  }

  const openRetraitModal = (gateway) => {
    setRetraitGateway(gateway || 'nita');
    setRetraitMontant("");
    setRetraitNote("");
    setRetraitDate(new Date().toISOString().slice(0, 10));
    setFormError("");
    setShowRetraitModal(true);
  };

  const handleCreateRetrait = async (e) => {
    e.preventDefault();
    setFormError("");
    const montant = parseFloat(retraitMontant);
    if (!montant || montant <= 0) {
      setFormError("Montant invalide.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/retraits/create.php`,
        { code_passerelle: retraitGateway, montant, date_retrait: retraitDate, note: retraitNote },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      if (res.data?.success) {
        setShowRetraitModal(false);
        fetchStats();
      } else {
        setFormError(res.data?.message || "Erreur lors de l'enregistrement.");
      }
    } catch (err) {
      setFormError(err.response?.data?.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const openListModal = async (gateway) => {
    setListGateway(gateway || null);
    setShowListModal(true);
    setLoadingList(true);
    try {
      const url = gateway
        ? `${API_BASE_URL}/retraits/list.php?code_passerelle=${gateway}`
        : `${API_BASE_URL}/retraits/list.php`;
      const res = await axios.get(url, { headers: authHeaders() });
      setRetraits(res.data?.retraits || []);
    } catch (err) {
      setRetraits([]);
    } finally {
      setLoadingList(false);
    }
  };

  const renderGatewayCard = (code) => {
    const meta = GATEWAY_META[code];
    const data = stats?.passerelles?.[code];
    if (!data) return null;
    const pctRetire = data.total_collecte > 0 ? Math.min(100, (data.total_retire / data.total_collecte) * 100) : 0;

    return (
      <div className="col-lg-6 mb-4" key={code}>
        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
          <div className="card-body p-4">
            <h5 className="mb-3 d-flex align-items-center" style={{ color: meta.color, fontWeight: '700' }}>
              <img src={meta.logo} alt={meta.label} style={{ height: '28px', objectFit: 'contain', marginRight: '0.5rem' }} />
              {meta.label}
            </h5>

            {[
              ['Total collecté', formatMontant(data.total_collecte), COLORS.primary],
              ['Transactions réussies', data.nb_transactions_reussies, COLORS.darkText],
              ['Total retiré', formatMontant(data.total_retire), COLORS.danger],
              ['Nb retraits', data.nb_retraits, COLORS.darkText],
              ['Restant chez partenaire', formatMontant(data.restant), COLORS.success],
            ].map(([label, value, color]) => (
              <div className="d-flex justify-content-between mb-2" key={label}>
                <span style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>{label}</span>
                <strong style={{ color, fontSize: '0.9rem' }}>{value}</strong>
              </div>
            ))}

            <div className="progress mt-2 mb-1" style={{ height: '6px', borderRadius: '3px' }}>
              <div className="progress-bar" style={{ width: `${pctRetire}%`, backgroundColor: meta.color }}></div>
            </div>
            <div className="mb-3" style={{ fontSize: '0.8rem', color: '#757575' }}>
              {pctRetire.toFixed(0)}% retiré
            </div>

            <div className="d-flex gap-2">
              <button className="btn btn-sm fw-semibold flex-fill" onClick={() => openRetraitModal(code)}
                style={{ color: meta.color, borderColor: meta.color, backgroundColor: 'transparent', borderRadius: '8px' }}>
                <i className="bi bi-plus-circle me-1"></i> Retrait
              </button>
              <button className="btn btn-sm fw-semibold flex-fill" onClick={() => openListModal(code)}
                style={{ color: COLORS.darkText, borderColor: '#e0e0e0', backgroundColor: 'transparent', borderRadius: '8px' }}>
                <i className="bi bi-list-ul me-1"></i> Voir retraits
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '700', fontSize: '1.8rem' }}>
          <i className="bi bi-briefcase me-2"></i>
          Dashboard Partenaires
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Tableau de bord</li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Dashboard Partenaires</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="d-flex justify-content-end gap-2 mb-4">
          <button className="btn fw-semibold" onClick={() => openRetraitModal('nita')}
            style={{ backgroundColor: COLORS.success, borderColor: COLORS.success, color: 'white', borderRadius: '8px' }}>
            <i className="bi bi-plus-circle me-1"></i> Nouveau Retrait
          </button>
          <button className="btn fw-semibold" onClick={() => openListModal(null)}
            style={{ borderColor: '#e0e0e0', color: COLORS.darkText, borderRadius: '8px' }}>
            <i className="bi bi-list-ul me-1"></i> Liste des Retraits
          </button>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4">
            {error}
            <button type="button" className="btn-close" onClick={() => setError("")}></button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: COLORS.primary }} role="status"></div>
          </div>
        ) : stats && (
          <>
            <div className="row mb-2">
              {[
                ['Total collecté (tous partenaires)', stats.total.total_collecte, COLORS.primary],
                ['Total retiré (tous partenaires)', stats.total.total_retire, COLORS.darkText],
                ['Restant chez les partenaires', stats.total.restant, COLORS.success],
              ].map(([label, value, color]) => (
                <div className="col-lg-4 mb-4" key={label}>
                  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                    <div className="card-body p-4">
                      <div style={{ color: COLORS.darkText, fontSize: '0.95rem', marginBottom: '0.5rem' }}>{label}</div>
                      <div style={{ color, fontSize: '1.8rem', fontWeight: '700' }}>{formatMontant(value)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="row">
              {renderGatewayCard('nita')}
              {renderGatewayCard('amana')}
            </div>
          </>
        )}
      </section>

      {/* Modal nouveau retrait */}
      {showRetraitModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: COLORS.primary, fontWeight: '600' }}>
                  Nouveau retrait
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowRetraitModal(false)}></button>
              </div>
              <form onSubmit={handleCreateRetrait}>
                <div className="modal-body">
                  {formError && <div className="alert alert-danger">{formError}</div>}

                  <div className="mb-3">
                    <label className="form-label">Passerelle</label>
                    <select className="form-select" value={retraitGateway} onChange={(e) => setRetraitGateway(e.target.value)}>
                      <option value="nita">NITA</option>
                      <option value="amana">Amana</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Montant (XOF)</label>
                    <input type="number" min="1" step="0.01" className="form-control" value={retraitMontant}
                      onChange={(e) => setRetraitMontant(e.target.value)} required />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Date du retrait</label>
                    <input type="date" className="form-control" value={retraitDate}
                      onChange={(e) => setRetraitDate(e.target.value)} required />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Note (optionnel)</label>
                    <textarea className="form-control" rows="2" value={retraitNote}
                      onChange={(e) => setRetraitNote(e.target.value)} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn" onClick={() => setShowRetraitModal(false)}>Annuler</button>
                  <button type="submit" className="btn fw-semibold" disabled={saving}
                    style={{ backgroundColor: COLORS.primary, borderColor: COLORS.primary, color: 'white' }}>
                    {saving ? <span className="spinner-border spinner-border-sm"></span> : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal liste des retraits */}
      {showListModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: COLORS.primary, fontWeight: '600' }}>
                  Retraits {listGateway ? `— ${GATEWAY_META[listGateway].label}` : '(toutes passerelles)'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowListModal(false)}></button>
              </div>
              <div className="modal-body">
                {loadingList ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" style={{ color: COLORS.primary }} role="status"></div>
                  </div>
                ) : retraits.length === 0 ? (
                  <p className="text-center text-muted py-4">Aucun retrait enregistré.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Passerelle</th>
                          <th>Montant</th>
                          <th>Note</th>
                          <th>Par</th>
                        </tr>
                      </thead>
                      <tbody>
                        {retraits.map((r) => (
                          <tr key={r.id_retrait}>
                            <td>{new Date(r.date_retrait).toLocaleDateString('fr-FR')}</td>
                            <td>{GATEWAY_META[r.code_passerelle]?.label || r.code_passerelle}</td>
                            <td>{formatMontant(r.montant)}</td>
                            <td>{r.note || '-'}</td>
                            <td>{r.effectue_par || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setShowListModal(false)}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default DashboardPartenaires;
